// Copyright 2018 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import { IPagedRequest, IPageResult, IWebRequest } from "libraries/WebRequestHandler/IWebRequestHandler";

export class PagedRequest<T> implements IPagedRequest<T> {
    private _total: T[] = [];
    private _finished: boolean;
    private _count: number;

    @computedFrom("_total")
    public get total(): T[] {
        return this._total;
    }

    @computedFrom("_finished")
    public get finished(): boolean {
        return this._finished;
    }

    private readonly baseRequest: IWebRequest<IPageResult<T>>;
    private readonly baseUrl: string;
    private pageUrl: string;
    private readonly postRequestAction: (T) => T;

    private requestPromise: Promise<unknown>;

    constructor(request: IWebRequest<IPageResult<T>>, postRequestAction: (T) => T) {
        this.baseRequest = request;
        this.baseUrl = this.baseRequest.url;
        this.requestPromise = Promise.resolve();
        this.postRequestAction = postRequestAction;

        this.pageUrl = this.baseUrl;
    }

    public next(): Promise<T[]> {
        // Await last request promise
        return (this.requestPromise = this.requestPromise.then(() => {
            if (this.finished) return Promise.resolve<T[]>([]);

            // Update request url
            this.baseRequest.withUrl(this.pageUrl);
            return this.baseRequest.send().then((response) => {
                this.pageUrl = response.content.NextPageLink;
                if (!this.pageUrl) {
                    this._finished = true;
                }

                if (this.postRequestAction) {
                    for (const item of response.content.Items) {
                        this.postRequestAction(item);
                    }
                }

                this._count = response.content.Count;
                this._total = this._total.concat(response.content.Items);
                return response.content.Items;
            });
        }) as Promise<T[]>);
    }

    public readToEnd(): Promise<T[]> {
        if (this._finished) {
            return Promise.resolve(this._total);
        }

        return this.next().then(() => {
            return this.readToEnd();
        });
    }

    public getCount(): Promise<number> {
        if (this._count != undefined) {
            return Promise.resolve(this._count);
        } else {
            return this.next().then(() => this._count);
        }
    }

    public static create<T>(request: IWebRequest<IPageResult<T>>, postRequest: (T) => T = undefined): IPagedRequest<T> {
        return new PagedRequest(request, postRequest);
    }
}
